/**
 * One shape for every way an AI Labs call can refuse.
 *
 * The collection defines ~20 `reason` codes across 400/403/404/409/429/502/503,
 * every one of them in a `{status, reason, message}` envelope. Without a
 * single model that becomes ~20 ad-hoc `catchError`s spread over a dozen
 * components, each deciding for itself whether a given code is an error.
 *
 * ENFORCEMENT: every method on AiLabsApiService ends with
 * `catchError((err) => throwError(toRefusal(err)))` — the RxJS 6 value
 * form, see the note on that method — so no caller ever
 * receives an HttpErrorResponse and no caller can be tempted to reach into
 * `err.error.reason` again. Subscribe blocks type their `error` parameter as
 * LabRefusal.
 */

/**
 * What the UI should DO about a refusal — which is a much smaller question
 * than which of twenty codes came back.
 *
 * `settled` is the one that earns this layer. `already_answered` on a quiz
 * question, or `practical_already_completed` on a resubmit, means the thing
 * the learner wanted is already true. Treated as a plain error it paints a red
 * toast over a correct action; treated as `settled` it refreshes the store and
 * says nothing.
 */
export type LabDisposition =
	/** No labs account / not a CAIRA learner / labs switched off here. Renders
	 *  the same set-up prompt that `is_provisioned: false` renders. */
	| "setup"
	/** An earlier step is unfinished. Inline "do X first", CTA points at it. */
	| "gate"
	/** The thing does not exist and that is normal — an unmapped assignment, a
	 *  lab with no quiz. Render NOTHING; never an error. */
	| "absent"
	/** Already true. Not a failure: refresh the owning store, info toast at most. */
	| "settled"
	/** Come back later. Render the server's countdown; never auto-retry. */
	| "waitr"
	/** The request itself was wrong or the grader said no. Relay the message. */
	| "rejected"
	/** Their side wobbled. Retryable. */
	| "transient";

export interface LabRefusal {
	kind: "refusal" | "network" | "server" | "unknown";
	/** 0 for a transport failure. */
	status: number;
	/** "" when the body carried none. */
	reason: string;
	/** ALWAYS non-empty and render-safe, so a template can bind it with no
	 *  `?.` chain and no fallback. That guarantee is the whole point. */
	message: string;
	disposition: LabDisposition;
	/** `cool_off_active` only. */
	coolOffMinutes?: number | null;
	coolOffEndsAt?: string | null;
	/** The untouched body. For logging — never for branching. */
	raw?: any;
}

/**
 * Codes whose message is written by the LABS SERVICE for the learner and must
 * reach them unedited. Their rubric knows which artefact kinds an assignment
 * accepts and we do not, so rewording it would replace a specific, actionable
 * sentence with a vague one.
 */
const VERBATIM_REASONS = new Set<string>(["AI_LABS_VALIDATION_REJECTED"]);

interface Rule {
	disposition: LabDisposition;
	copy: string | ((r: LabRefusal) => string);
}

/**
 * reason → what to do + what to say.
 *
 * Copy lives here rather than in templates so the same refusal reads the same
 * wherever it surfaces — `no_ai_lab_enrollment` can arrive from six different
 * endpoints.
 *
 * ⚠️ WORDING CONSTRAINT: CairaAuthInterceptor treats a 403 as a session expiry
 * when the stringified body matches
 * `expired|signature|invalid token|credentials|authentication|unauthor`, and
 * it matches the WHOLE body including `reason` keys. None of today's server
 * messages trip it, and none of the copy below may either — a 403 that does
 * would silently log the learner out of the LMS. See the allow-list in
 * caira-auth.interceptor.ts, which is the belt to this file's braces.
 */
const RULES: Record<string, Rule> = {
	// --- setup ---
	caira_access_required: {
		disposition: "setup",
		copy: "AI Labs is part of CAIRA. Enrol in CAIRA to open the lab.",
	},
	no_sso_handle: {
		disposition: "setup",
		copy: "Your account isn't linked to Miles SSO yet, so the lab can't be reached. Please contact support.",
	},
	AI_LABS_NOT_PROVISIONED: {
		disposition: "setup",
		copy: "Set up your AI Lab account to continue.",
	},
	AI_LABS_LMS_UNCONFIGURED: {
		disposition: "setup",
		copy: "The AI Labs practical isn't available in this environment.",
	},

	// --- gate ---
	final_assessment_not_passed: {
		disposition: "gate",
		copy: "Pass the final assessment before submitting your practical.",
	},
	video_not_complete: {
		disposition: "gate",
		copy: "Finish the lab video first.",
	},
	no_ai_lab_enrollment: {
		disposition: "gate",
		copy: "Start this lab before you can do that.",
	},

	// --- absent: normal states, rendered as nothing ---
	ai_lab_not_found: { disposition: "absent", copy: "" },
	no_quiz_config: { disposition: "absent", copy: "" },
	ai_lab_assignment_not_configured: {
		disposition: "absent",
		// Not the learner's doing — an admin hasn't mapped the assignment yet.
		copy: "This lab's practical isn't set up yet. Our team has been notified.",
	},

	// --- settled: already true, therefore not failures ---
	already_completed: { disposition: "settled", copy: "You've already done this." },
	already_answered: { disposition: "settled", copy: "" },
	practical_already_completed: {
		disposition: "settled",
		copy: "You've already completed this practical.",
	},
	credit_already_earned_via_course: {
		disposition: "settled",
		copy: "You've already earned this course's CPE, so its labs can't be started.",
	},
	// The mirror of the above, and the one Ops asked about: the learner is
	// part-way through the parent masterclass course, so its labs are shut.
	// `settled` rather than `transient` — without the entry an unknown
	// reason falls through to "try again shortly", which is the wrong
	// invitation for something no amount of retrying will clear. The
	// server's own remark names the course, so the copy here is only the
	// fallback for a body that arrives without one.
	started_via_masterclass_course: {
		disposition: "settled",
		copy: "You've already started this course on CAIRA Courses, so its AI Labs aren't available.",
	},
	// The 409 the SAME condition raises on `POST /ai-labs/course/<id>/start/`.
	// The contract uses a different code there than on `lock_state`, and an
	// unknown one falls through to `transient` — "try again shortly" for a
	// lock no retry will clear.
	locked_by_masterclass_course: {
		disposition: "settled",
		copy: "You've already started this course on CAIRA Courses, so its AI Labs aren't available.",
	},
	tokens_request_open: {
		disposition: "settled",
		copy: "You already have a credits request waiting for approval.",
	},

	// --- wait ---
	cool_off_active: {
		disposition: "waitr",
		copy: (r) =>
			r.coolOffMinutes && r.coolOffMinutes > 0
				? `You can try again in ${formatWait(r.coolOffMinutes)}.`
				: "You can try again shortly.",
	},
	AI_LABS_RATE_LIMITED: {
		disposition: "waitr",
		copy: "Too many attempts just now. Give it a minute and try again.",
	},

	// --- rejected ---
	answer_count_mismatch: {
		disposition: "rejected",
		copy: "Answer every question before submitting.",
	},
	invalid_question: {
		disposition: "rejected",
		copy: "That question isn't part of this lab. Reload and try again.",
	},
	invalid_question_id: {
		disposition: "rejected",
		copy: "That question isn't part of this form. Reload and try again.",
	},
	invalid_body: {
		disposition: "rejected",
		copy: "That request wasn't accepted. Reload and try again.",
	},
	invalid_query: {
		disposition: "rejected",
		copy: "That request wasn't accepted. Reload and try again.",
	},

	// --- transient ---
	AI_LABS_CALL_FAILED: {
		disposition: "transient",
		copy: "The lab service didn't answer. Try again shortly.",
	},
	AI_LABS_ASSIGNMENT_MISMATCH: {
		disposition: "transient",
		copy: "This lab's practical is misconfigured. Our team has been notified.",
	},
};

/** Every reason code this app knows about. Imported by the interceptor so a
 *  labs refusal is never mistaken for a dead session. */
export const LAB_REASON_CODES: ReadonlySet<string> = new Set(Object.keys(RULES));

function formatWait(minutes: number): string {
	if (minutes < 60) {
		return `${minutes} minute${minutes === 1 ? "" : "s"}`;
	}
	const hours = Math.round(minutes / 60);
	return `${hours} hour${hours === 1 ? "" : "s"}`;
}

/** Generic copy when nothing more specific is known. */
function fallbackFor(status: number): { d: LabDisposition; copy: string } {
	if (status === 0) {
		return { d: "transient", copy: "You appear to be offline. Try again shortly." };
	}
	if (status === 429) {
		return { d: "waitr", copy: "Too many attempts just now. Try again shortly." };
	}
	if (status >= 500) {
		return { d: "transient", copy: "Something went wrong at our end. Try again shortly." };
	}
	if (status === 404) {
		return { d: "absent", copy: "" };
	}
	if (status === 403) {
		return { d: "gate", copy: "You can't do that yet." };
	}
	return { d: "rejected", copy: "That request wasn't accepted." };
}

/**
 * Is this string a message meant for a person?
 *
 * Guards the pass-through branch: a reason code we've never seen should still
 * show the server's own sentence rather than a generic one, but an HTML error
 * page or a stack trace must not reach a learner.
 */
function looksRenderable(value: any): value is string {
	return (
		typeof value === "string" &&
		value.trim().length > 0 &&
		value.length < 200 &&
		value.indexOf("<") === -1
	);
}

/**
 * Turn any HttpErrorResponse into a LabRefusal.
 *
 * Message precedence, in order:
 *   1. a verbatim-relay code → the server's message, unedited
 *   2. a known code → our copy
 *   3. an unknown code with a plausible server message → that message, so a
 *      reason the backend adds later is still legible without a release here
 *   4. generic copy keyed on the HTTP status
 */
/**
 * Say "CAIRA Courses", which is what the product is called on this side.
 *
 * The labs backend writes "…on Masterclass" into `lock_remark`, naming the
 * course it came from the way the labs team knows it. Learners here have
 * never seen that word. The prose is still theirs — this only renames the
 * product in it, at the point of render.
 *
 * ⚠️ NARROW ON PURPOSE. It matches the phrase "on Masterclass", not the
 * bare word: the remark also carries the COURSE TITLE, and a title with
 * "Masterclass" in it would otherwise be rewritten too. The real fix is
 * the backend changing its own copy; delete this when it does.
 */
export function inCairaWords(text: string): string {
	if (!text) {
		return "";
	}
	return text.replace(/\bon (?:the )?Masterclass\b/g, "on CAIRA Courses");
}

export function toRefusal(err: any): LabRefusal {
	// Duck-typed rather than `instanceof HttpErrorResponse`, for two reasons:
	// it keeps this module free of an Angular import so it stays pure and
	// directly testable, and it degrades correctly on anything else that gets
	// thrown — a plain Error has no `status`, so it reads as a transport
	// failure rather than crashing the mapper.
	const isHttp = err && typeof err.status === "number";
	const status: number = isHttp ? err.status : 0;
	const body: any = isHttp ? err.error : null;
	const reason =
		body && typeof body === "object" && typeof body.reason === "string"
			? body.reason
			: "";

	const refusal: LabRefusal = {
		kind: status === 0 ? "network" : status >= 500 ? "server" : reason ? "refusal" : "unknown",
		status,
		reason,
		message: "",
		disposition: "transient",
		coolOffMinutes:
			body && typeof body.cool_off_minutes_remaining === "number"
				? body.cool_off_minutes_remaining
				: null,
		coolOffEndsAt:
			body && typeof body.cool_off_ends_at === "string"
				? body.cool_off_ends_at
				: null,
		raw: body,
	};

	const rule = reason ? RULES[reason] : undefined;

	if (rule) {
		refusal.disposition = rule.disposition;
		refusal.message =
			typeof rule.copy === "function" ? rule.copy(refusal) : rule.copy;
	} else {
		const fb = fallbackFor(status);
		refusal.disposition = fb.d;
		refusal.message = fb.copy;
	}

	// A verbatim code outranks our copy — but only if they actually sent words.
	if (VERBATIM_REASONS.has(reason) && looksRenderable(body?.message)) {
		refusal.message = body.message;
		refusal.disposition = "rejected";
	} else if (!rule && looksRenderable(body?.message)) {
		// Unknown code, plausible sentence: theirs beats our generic.
		refusal.message = body.message;
	}

	// The non-empty guarantee. `absent` refusals render nothing, so an empty
	// message there is deliberate — but it must still never be undefined.
	if (!refusal.message && refusal.disposition !== "absent") {
		refusal.message = fallbackFor(status).copy || "Something went wrong.";
	}

	// One boundary for every refusal sentence, including the two paths above
	// where the server's own words win over ours.
	refusal.message = inCairaWords(refusal.message);

	return refusal;
}

/** Narrowing helper for `catchError` blocks that only care about one case. */
export function isDisposition(
	err: any,
	...dispositions: LabDisposition[]
): boolean {
	return !!err && dispositions.indexOf(err.disposition) !== -1;
}
